
import React, { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { format } from "date-fns";
import { useChangelog, ChangelogEntry, ChangeItem } from "@/hooks/useChangelog";
import { useTranslation } from "react-i18next";
import { Sparkles, Wrench, Bug, AlertTriangle, Calendar } from "lucide-react";

interface ChangelogDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  highlightId?: string;
}

const changeTypeConfig: Record<ChangeItem["type"], { icon: React.ElementType; label: string; className: string }> = {
  feature: { icon: Sparkles, label: "Feature", className: "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800" },
  improvement: { icon: Wrench, label: "Improvement", className: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800" },
  fix: { icon: Bug, label: "Fix", className: "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800" },
  breaking: { icon: AlertTriangle, label: "Breaking", className: "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800" },
};

export function ChangelogDialog({ open, onOpenChange, highlightId }: ChangelogDialogProps) {
  const { t } = useTranslation();
  const { useChangelogsQuery, useMarkAllChangelogsAsReadMutation } = useChangelog();
  const { data: changelogs = [], isLoading } = useChangelogsQuery();
  const markAllAsRead = useMarkAllChangelogsAsReadMutation();

  useEffect(() => {
    if (open && changelogs.length > 0) {
      markAllAsRead.mutate();
    }
  }, [open, changelogs.length]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] p-0">
        <DialogHeader className="p-6 pb-2">
          <DialogTitle className="text-xl font-bold">
            {t('changelog.title', 'Changelog')}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            {t('changelog.subtitle', 'See what\'s new and improved')}
          </p>
        </DialogHeader>
        <ScrollArea className="max-h-[65vh] px-6 pb-6">
          {isLoading ? (
            <div className="py-8 text-center text-muted-foreground">
              {t('changelog.loading', 'Loading changelog...')}
            </div>
          ) : changelogs.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              {t('changelog.empty', 'No changelogs available yet.')}
            </div>
          ) : (
            <div className="space-y-6">
              {changelogs.map((changelog, index) => (
                <div key={changelog.id}>
                  <ChangelogVersionBlock
                    changelog={changelog}
                    isHighlighted={changelog.id === highlightId}
                  />
                  {index < changelogs.length - 1 && <Separator className="mt-6" />}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

function ChangelogVersionBlock({ changelog, isHighlighted }: { changelog: ChangelogEntry; isHighlighted: boolean }) {
  return (
    <div className={`rounded-lg transition-colors ${isHighlighted ? "bg-primary/5 p-4 -mx-2 ring-1 ring-primary/20" : ""}`}>
      <div className="flex items-center gap-3 mb-2">
        <Badge variant="outline" className="font-mono text-sm px-2.5 py-0.5">
          v{changelog.version}
        </Badge>
        <span className="text-xs text-muted-foreground flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          {format(changelog.publishedAt, "MMM d, yyyy")}
        </span>
      </div>
      <h3 className="font-semibold text-base mb-1">{changelog.title}</h3>
      {changelog.description && (
        <p className="text-sm text-muted-foreground mb-3">{changelog.description}</p>
      )}
      <ul className="space-y-2">
        {changelog.changes.map((change, idx) => {
          const config = changeTypeConfig[change.type] || changeTypeConfig.improvement;
          const Icon = config.icon;
          return (
            <li key={idx} className="flex items-start gap-2 text-sm">
              <Badge variant="outline" className={`text-[10px] px-1.5 py-0 mt-0.5 shrink-0 ${config.className}`}>
                <Icon className="h-3 w-3 mr-0.5" />
                {config.label}
              </Badge>
              <span>{change.text}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
